/**
 * Loading Component — DineLink branded spinner
 * Displays the DineLink logo with animated ring and scanner sweep.
 */

import { cn } from '@/lib/utils';

const sizeMap = {
  sm: 'w-10 h-10',
  md: 'w-20 h-20',
  lg: 'w-36 h-36',
};

const ringSizeMap = {
  xs: 'w-4 h-4',
  sm: 'w-5 h-5',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
};

/**
 * LoadingRing — Just the DineLink spinning gradient ring.
 * Lightweight variant for inline use (buttons, badges, etc.).
 */
export function LoadingRing({ className = '', size = 'sm' }) {
  return (
    <div
      className={cn(
        'rounded-full animate-[dlSpin_1.25s_linear_infinite] motion-reduce:animate-none',
        ringSizeMap[size] || ringSizeMap.sm,
        className,
      )}
      style={{
        background: 'conic-gradient(from 0deg, rgba(14,115,227,0.1) 0deg, rgba(14,115,227,0.95) 105deg, rgba(242,159,5,0.92) 160deg, rgba(14,115,227,0.16) 260deg, rgba(14,115,227,0.1) 360deg)',
        mask: 'radial-gradient(circle, transparent 52%, #000 54%, #000 70%, transparent 72%)',
        WebkitMask: 'radial-gradient(circle, transparent 52%, #000 54%, #000 70%, transparent 72%)',
      }}
      aria-hidden="true"
    />
  );
}

export function Loading({ className = '', size = 'md' }) {
  return (
    <div className={cn('flex items-center justify-center', className)}>
      <div className={cn('relative grid place-items-center', sizeMap[size] || sizeMap.md)}>
        {/* Spinning gradient ring */}
        <div
          className="absolute -inset-[8%] rounded-full animate-[dlSpin_1.25s_linear_infinite] motion-reduce:animate-none"
          style={{
            background: 'conic-gradient(from 0deg, rgba(14,115,227,0.1) 0deg, rgba(14,115,227,0.95) 105deg, rgba(242,159,5,0.92) 160deg, rgba(14,115,227,0.16) 260deg, rgba(14,115,227,0.1) 360deg)',
            mask: 'radial-gradient(circle, transparent 59%, #000 60%, #000 68%, transparent 69%)',
            WebkitMask: 'radial-gradient(circle, transparent 59%, #000 60%, #000 68%, transparent 69%)',
          }}
          aria-hidden="true"
        />
        {/* Static glass disc */}
        <div
          className="absolute inset-[5%] rounded-full border border-primary/25"
          style={{
            background: 'radial-gradient(circle at 42% 30%, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.8) 44%, rgba(255,255,255,0.45) 72%, rgba(255,255,255,0.2) 100%)',
          }}
          aria-hidden="true"
        />
        {/* Logo SVG */}
        <svg
          viewBox="0 0 1024 1024"
          className="w-[79%] aspect-square z-[2] overflow-visible animate-[dlBreathe_2.2s_ease-in-out_infinite] motion-reduce:animate-none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="dlScanGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#0e73e3" stopOpacity="0.06" />
              <stop offset="50%" stopColor="#0e73e3" stopOpacity="1" />
              <stop offset="100%" stopColor="#f29f05" stopOpacity="0.95" />
            </linearGradient>
          </defs>
          <circle
            className="animate-[dlSweep_1.55s_linear_infinite] motion-reduce:animate-none"
            cx="512" cy="512" r="438"
            fill="none"
            stroke="url(#dlScanGrad)"
            strokeWidth="16"
            strokeLinecap="round"
            strokeDasharray="72 1000"
            style={{ transformOrigin: '50% 50%' }}
          />
          <g className="animate-[dlBreathe_2.2s_ease-in-out_infinite] motion-reduce:animate-none" style={{ transformOrigin: '50% 50%' }}>
            <path
              fill="#f29f05"
              d="m 342.72367,520.57321 c -22.29,14.81 -45.03,29.68 -67.1,44.52 -66.24,44.46 -126.71,88.76 -151.5,131.58 l 70.93,-401.76 c 13.48,45.9 54.45,102.85 98.64,161.13 16.32,21.48 33.05,43.16 49.03,64.53 z"
            />
            <path
              fill="#0E73E3"
              stroke="#0E73E3"
              strokeWidth="12"
              strokeLinejoin="round"
              style={{ paintOrder: 'stroke fill' }}
              d="m 882.40367,439.59321 c 0,20.86 -1.9,43.29 -5.69,67.3 -11.38,63.85 -35.89,121.35 -73.48,172.55 -37.62,51.2 -85.33,91.5 -143.16,120.89 -57.83,29.39 -197.65,44.09 -267.81,44.09 h -177.37 c -15.32,0 -30.44,-4.3 -43.08,-12.96 -75.509998,-51.73 69.44,-162.86 201.39,-270.5 l -16.97,95.74 h 50.25 c 47.41,0 163.2,-12.96 195.77,-38.88 32.54,-25.9 52.93,-62.88 61.15,-110.92 1.9,-12.63 2.85,-24.32 2.85,-35.08 0,-37.28 -11.55,-65.73 -34.61,-85.33 -23.09,-19.58 -55.16,-29.39 -96.24,-29.39 h -126.08 l -23.65,133.45 c -117.19,-153.52 -184.35,-276.87 -103.75,-311.09 10.14,-4.31 21.13,-6.29 32.15,-6.29 h 272.53 c 61.94,0 115.04,11.24 159.28,33.66 44.23,22.45 78.05,53.74 101.45,93.87 23.37,40.14 35.08,86.43 35.08,138.89 z"
            />
          </g>
        </svg>
      </div>
    </div>
  );
}

export default Loading;
